import type {
  ListStationDevicesResponse,
  ProvisionStationDeviceRequest,
  ProvisionStationDeviceResponse,
  StationLookupResponse,
  StationSessionRequest,
  StationSessionResponse,
} from '@jq4race/shared';
import { getBaseUrl, request } from './client.js';
import { ApiError, type AuthErrorCode } from './errors.js';

// valet-keychain-qr D1 — station-auth. Deliberately NOT built on top of
// request()/configureClient() in client.ts: that singleton client carries a
// SINGLE operator identity (access token + refresh-cookie flow + a 401
// handler that redirects to /login on session expiry). A station device has
// no personal login at all (spec "no personal valet login required") and
// its own distinct token lifecycle (opaque device token → short-lived
// station JWT, re-minted by calling issueStationSession again — never a
// refresh-token flow). Mixing the two identities into one client risks a
// station 401 silently triggering the OPERATOR session-expired redirect.
// These two functions do their own minimal fetch instead.

function mapStatus(status: number): AuthErrorCode {
  if (status === 401) return 'invalid_credentials';
  if (status === 429) return 'rate_limited';
  return 'unknown';
}

async function parseAndThrow(res: Response): Promise<never> {
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  const serverCode = (payload as { code?: AuthErrorCode } | null)?.code;
  const code = serverCode ?? mapStatus(res.status);
  const message = (payload as { message?: string } | null)?.message ?? `HTTP ${res.status}`;
  throw new ApiError(code, message, res.status, payload, serverCode === undefined);
}

/** Exchanges an opaque device token for a short-lived station session. No auth header — this IS the login step. */
export async function issueStationSession(
  token: StationSessionRequest['token'],
): Promise<StationSessionResponse> {
  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}/station/session`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed');
  }
  if (!res.ok) return parseAndThrow(res);
  return (await res.json()) as StationSessionResponse;
}

/** Read-only lookup, scoped server-side to the station JWT's own locationId. */
export async function stationLookup(
  accessToken: string,
  query: string,
): Promise<StationLookupResponse> {
  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}/station/tickets/lookup?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: { authorization: `Bearer ${accessToken}` },
    });
  } catch (err) {
    throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed');
  }
  if (!res.ok) return parseAndThrow(res);
  return (await res.json()) as StationLookupResponse;
}

// -- Admin: station device management (org_admin) --
// These use the regular operator client (admin console = personal org_admin login).

export async function listStationDevices(locationId: string): Promise<ListStationDevicesResponse> {
  return request<ListStationDevicesResponse>(
    `/admin/locations/${encodeURIComponent(locationId)}/station-devices`,
    { method: 'GET' },
  );
}

/** The plaintext `token` is returned ONLY by this call and can never be retrieved again. */
export async function provisionStationDevice(
  locationId: string,
  input: ProvisionStationDeviceRequest,
): Promise<ProvisionStationDeviceResponse> {
  return request<ProvisionStationDeviceResponse>(
    `/admin/locations/${encodeURIComponent(locationId)}/station-devices`,
    { method: 'POST', body: input },
  );
}

export async function revokeStationDevice(locationId: string, deviceId: string): Promise<void> {
  await request<void>(
    `/admin/locations/${encodeURIComponent(locationId)}/station-devices/${encodeURIComponent(deviceId)}`,
    { method: 'DELETE' },
  );
}
